import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const ContainerPage: Component = () => {
  return (
    <>
      <Title>Container Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Container</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            El cimiento de la arquitectura de diseño. Los contenedores centran y limitan el ancho del contenido en pantallas grandes, asegurando una lectura legible y una estructura coherente en toda
            la aplicación.
          </p>
        </div>

        <div class="space-y-12">
          {/* Concepto Base */}
          <Card>
            <Card.Header
              title="Alineación y Centrado"
              subtitle="El contenedor aplica márgenes automáticos y un ancho máximo de 1280px (7xl) por defecto."
              icon={<Icon icon="material-symbols-light:fullscreen" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8 p-10 bg-surface-muted rounded-xl border border-border shadow-inner">
                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Visualización de Límites</h4>
                  <div class="relative p-6 border-2 border-dashed border-border bg-surface rounded-xl text-center">
                    <Container>
                      <div class="py-10 bg-primary-500/10 rounded-lg text-primary-600 font-bold border border-primary-500/20">ZONA DE CONTENIDO SEGURO</div>
                    </Container>
                    <div class="absolute -top-3 left-1/2 -translate-x-1/2 px-4 bg-surface text-[10px] uppercase font-black text-foreground-subtle border border-border rounded-full">
                      mx-auto max-w-7xl
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="flex items-start gap-4 p-6 bg-surface rounded-xl shadow-sm border border-border">
                    <div class="p-3 bg-primary-soft rounded-lg">
                      <Icon icon="material-symbols-light:monitor" class="text-primary-600" />
                    </div>
                    <div>
                      <h4 class="font-bold text-sm">Escritorio (Desktop)</h4>
                      <p class="text-xs text-foreground-subtle italic">Limita a 1280px para evitar líneas de texto demasiado largas.</p>
                    </div>
                  </div>
                  <div class="flex items-start gap-4 p-6 bg-surface rounded-xl shadow-sm border border-border">
                    <div class="p-3 bg-surface-muted rounded-lg">
                      <Icon icon="material-symbols-light:smartphone-outline" class="text-secondary-600" />
                    </div>
                    <div>
                      <h4 class="font-bold text-sm">Móvil (Mobile)</h4>
                      <p class="text-xs text-foreground-subtle italic">Aplica paddings laterales automáticos (16px - 32px) para separar el contenido del borde físico.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Personalización y Ancho Completo */}
          <Card>
            <Card.Header
              title="Ajustes de Clase y Variantes de Ancho"
              subtitle="Sobrescribe el comportamiento por defecto mediante utilidades de Tailwind."
              icon={<Icon icon="material-symbols-light:settings-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-12">
                <div class="p-8 bg-surface-muted rounded-xl border border-border flex flex-col gap-6">
                  <div class="p-4 bg-surface border rounded-xl">
                    <h4 class="text-xs font-bold text-foreground-subtle mb-4 uppercase">Ancho Completo (max-w-none)</h4>
                    <Container class="max-w-none px-0">
                      <div class="w-full h-12 bg-indigo-500/20 rounded flex items-center justify-center border border-indigo-500/30 text-indigo-600 font-bold italic">FULL WIDTH CONTAINER</div>
                    </Container>
                  </div>

                  <div class="p-4 bg-surface border rounded-xl">
                    <h4 class="text-xs font-bold text-foreground-subtle mb-4 uppercase">Compacto (max-w-4xl)</h4>
                    <Container class="max-w-4xl">
                      <div class="w-full h-12 bg-amber-500/20 rounded flex items-center justify-center border border-amber-500/30 text-amber-600 font-bold italic">CENTERED COMPACT (896px)</div>
                    </Container>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-slate-900 p-10 rounded-3xl overflow-hidden relative">
                  <div class="absolute inset-0 opacity-10 pointer-events-none grayscale contrast-125">
                    <Icon icon="material-symbols-light:science-outline" class="text-white absolute -right-10 -bottom-10" size={240} />
                  </div>
                  <div class="space-y-4 relative z-10">
                    <h3 class="text-2xl font-black text-white italic tracking-tighter">DISEÑO RESPONSIVO SISTEMÁTICO</h3>
                    <p class="text-foreground-subtle text-sm leading-relaxed">
                      Al usar el componente <span class="bg-primary-500/20 px-1 rounded text-primary-400">Container</span> en lugar de divs manuales, aseguras que todas tus vistas mantengan la misma
                      alineación que el resto de la aplicación, optimizando la carga cognitiva del usuario.
                    </p>
                    <Button color="primary" variant="soft" size="sm" startIcon="material-symbols-light:content-copy-outline">
                      Copiar Patrón
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Propiedades y atributos del componente Container."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">children</td>
                      <td class="px-6 py-4 italic">JSX.Element</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance">Cualquier contenido Reactivo (SolidJS) a ser centrado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">class</td>
                      <td class="px-6 py-4 italic">string</td>
                      <td class="px-6 py-4 font-mono text-xs">"mx-auto w-full max-w-7xl px-4 md:px-8"</td>
                      <td class="px-6 py-4 text-balance">Sobrescribe los estilos base de centrado, ancho y padding.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold opacity-60 italic">...props</td>
                      <td class="px-6 py-4 italic">HTMLAttributes</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance opacity-80 italic">Propiedades estándar de un div de HTML.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ContainerPage
